import { openDB, type IDBPDatabase } from "idb";

export type DraftStatus = "draft" | "structured" | "published";
export type DraftType = "sop" | "blank";

export interface Draft {
  id: string;
  title: string;
  content: string;
  updatedAt: number;
  status?: DraftStatus;
  rawDictation?: string;
  markdownSource?: string;
  type?: DraftType;
}

export interface DraftVersion {
  id: string;
  draftId: string;
  content: string;
  markdownSource?: string;
  status: DraftStatus;
  label: string;
  timestamp: number;
}

const DB_NAME = "sop-dictation";
const DRAFTS_STORE = "drafts";
const VERSIONS_STORE = "versions";
const SURVEYS_STORE = "surveys";
const DB_VERSION = 3;

let dbPromise: Promise<IDBPDatabase> | null = null;

function getDB() {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(DRAFTS_STORE)) {
          db.createObjectStore(DRAFTS_STORE, { keyPath: "id" });
        }
        if (!db.objectStoreNames.contains(VERSIONS_STORE)) {
          const store = db.createObjectStore(VERSIONS_STORE, { keyPath: "id" });
          store.createIndex("byDraft", "draftId");
        }
        if (!db.objectStoreNames.contains(SURVEYS_STORE)) {
          db.createObjectStore(SURVEYS_STORE, { keyPath: "id" });
        }
      },
    });
  }
  return dbPromise;
}

export async function saveDraft(draft: Draft): Promise<void> {
  const db = await getDB();
  await db.put(DRAFTS_STORE, draft);
}

export async function getDraft(id: string): Promise<Draft | undefined> {
  const db = await getDB();
  return db.get(DRAFTS_STORE, id);
}

export async function getAllDrafts(): Promise<Draft[]> {
  const db = await getDB();
  const drafts = await db.getAll(DRAFTS_STORE);
  return drafts.sort((a, b) => b.updatedAt - a.updatedAt);
}

export async function deleteDraft(id: string): Promise<void> {
  const db = await getDB();
  await db.delete(DRAFTS_STORE, id);
}

export async function saveVersion(
  draftId: string,
  content: string,
  label: string,
  status: DraftStatus = "draft",
  markdownSource?: string
): Promise<void> {
  const db = await getDB();
  const version: DraftVersion = {
    id: `${draftId}-${Date.now()}`,
    draftId,
    content,
    markdownSource,
    status,
    label,
    timestamp: Date.now(),
  };
  await db.put(VERSIONS_STORE, version);
}

export async function getVersions(draftId: string): Promise<DraftVersion[]> {
  const db = await getDB();
  const versions = await db.getAllFromIndex(VERSIONS_STORE, "byDraft", draftId);
  return versions.sort((a, b) => b.timestamp - a.timestamp);
}

export async function deleteVersions(draftId: string): Promise<void> {
  const db = await getDB();
  const versions = await db.getAllFromIndex(VERSIONS_STORE, "byDraft", draftId);
  const tx = db.transaction(VERSIONS_STORE, "readwrite");
  for (const v of versions) {
    tx.store.delete(v.id);
  }
  await tx.done;
}

// Export/Import

export interface ExportData {
  version: 1;
  exportedAt: number;
  drafts: Draft[];
  versions: DraftVersion[];
}

export async function exportAllDrafts(): Promise<ExportData> {
  const db = await getDB();
  const drafts = await db.getAll(DRAFTS_STORE);
  const versions = await db.getAll(VERSIONS_STORE);
  return {
    version: 1,
    exportedAt: Date.now(),
    drafts,
    versions,
  };
}

export async function importDrafts(
  data: ExportData
): Promise<{ imported: number; skipped: number }> {
  if (!data || data.version !== 1 || !Array.isArray(data.drafts)) {
    throw new Error("Invalid export file format");
  }

  const db = await getDB();
  let imported = 0;
  let skipped = 0;

  // Import drafts — skip if local copy is newer
  for (const draft of data.drafts) {
    const existing = await db.get(DRAFTS_STORE, draft.id);
    if (existing && existing.updatedAt >= draft.updatedAt) {
      skipped++;
      continue;
    }
    await db.put(DRAFTS_STORE, draft);
    imported++;
  }

  // Import versions — add missing ones by ID
  if (Array.isArray(data.versions)) {
    for (const version of data.versions) {
      const existing = await db.get(VERSIONS_STORE, version.id);
      if (!existing) {
        await db.put(VERSIONS_STORE, version);
      }
    }
  }

  return { imported, skipped };
}
